import type { Embedder } from './types.js';

/**
 * Offline, deterministic, dependency-free embedder: hashes tokens into a fixed
 * bag-of-features vector with sublinear TF weighting, L2-normalized so a dot
 * product is cosine similarity. Crude vs a real model (it's lexical, not
 * semantic), but it needs no API key and never sends source off the machine —
 * the right default for embedded mode and "never upload" repos. Production uses
 * {@link OpenAiEmbedder}.
 */
const DIM = 512;

/** FNV-1a 32-bit hash → bucket index. */
function bucket(token: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < token.length; i++) {
    h ^= token.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0) % DIM;
}

function tokenize(text: string): string[] {
  return text.toLowerCase().match(/[a-z0-9_]{2,}/g) ?? [];
}

function embedOne(text: string): number[] {
  const vec = new Array<number>(DIM).fill(0);
  for (const token of tokenize(text)) {
    const b = bucket(token);
    vec[b] = (vec[b] ?? 0) + 1;
  }
  // sublinear TF
  for (let i = 0; i < DIM; i++) {
    const v = vec[i] ?? 0;
    vec[i] = v > 0 ? 1 + Math.log(v) : 0;
  }
  // L2 normalize
  let norm = 0;
  for (let i = 0; i < DIM; i++) {
    const v = vec[i] ?? 0;
    norm += v * v;
  }
  norm = Math.sqrt(norm) || 1;
  for (let i = 0; i < DIM; i++) {
    vec[i] = (vec[i] ?? 0) / norm;
  }
  return vec;
}

export class LocalEmbedder implements Embedder {
  // Dimension is part of the id so any DIM change invalidates stale indexes by
  // id mismatch rather than silently mis-scoring against a different-length vector.
  readonly id = `local-hash-v1-d${DIM}`;
  readonly dimensions = DIM;

  async embed(texts: readonly string[]): Promise<number[][]> {
    return texts.map(embedOne);
  }
}
